/**
 * HandoffCenterPage — DIGEMP-HANDOFF-CENTER-FRONTEND-084.
 *
 * One operator surface for every conversation that moved between a technician and a Digital
 * Employee: where it went, what the employee found, what is still waiting on a human, and which
 * evidence was recorded. Continuation is one click away into the surfaces that already own the
 * work — the conversation itself (ConversationPanel), the employee workspace, and the ticket.
 *
 * No new backend surface: the page reads conversationsApi.list() + conversationsApi.listMessages()
 * and projects them through handoffCenterModel. It never writes, never routes and never approves —
 * those stay with ConversationPanel and the existing run endpoints.
 *
 * Permission model is the existing one, unchanged: agents_view to see the center, and the
 * technical evidence drawer follows ConversationPanel's rule (agents_manage only). Operators
 * without agents_manage still get the truthful "evidence recorded / not recorded" summary — just
 * without tool names, statuses or summaries.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { conversationsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar from "../components/EmployeeAvatar";
import {
  HANDOFF_DIRECTION_LABELS,
  buildHandoffCenter,
  handoffDirection,
  handoffEmployeeName,
  isAwaitingOperator,
  isHandoffCandidate,
} from "../components/handoffCenterModel";
import { traceEvidenceSourceLabel } from "../components/evidenceSourceVocabulary";
import { traceToolLabel } from "../components/toolNameVocabulary";
import { traceErrorLabel } from "../components/errorReasonVocabulary";

const CONVERSATION_SCAN_LIMIT = 60;
const MESSAGE_FETCH_LIMIT = 40;
// The list is cheap; a transcript fetch is not, so it refreshes at half the rate.
const LIST_POLL_MS = 30000;
const TRANSCRIPT_POLL_MS = 60000;

const TRACE_STATUS_LABELS = {
  SUCCEEDED: "הושלם",
  FAILED: "נכשל",
  PROPOSED: "ממתין לביצוע",
  APPROVED: "אושר",
};

const TABS = [
  { value: "all", label: "הכל" },
  { value: "incoming", label: "נכנסות — ממתין לי" },
  { value: "outgoing", label: "יוצאות לעובד" },
];

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("he-IL");
  } catch {
    return "";
  }
}

function SummaryTile({ label, value, tone, hint }) {
  const toneClass =
    tone === "amber" ? "border-amber-200 bg-amber-50 text-amber-900"
    : tone === "blue" ? "border-blue-200 bg-blue-50 text-blue-900"
    : tone === "gray" ? "border-gray-200 bg-white text-gray-700"
    : "border-gray-200 bg-white text-gray-700";
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneClass}`} title={hint} role="group" aria-label={label}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function EvidenceDrawer({ event, canManage }) {
  const [open, setOpen] = useState(false);
  const evidence = event.evidence;

  if (!canManage) {
    // Gate preserved: no tool names, statuses or summaries for non-managers — only whether the
    // employee's work left an auditable record at all.
    return (
      <p className="text-xs text-gray-500">
        ראיה מתועדת: {evidence ? "כן" : "לא תועדה ראיה לעבודה זו"}
      </p>
    );
  }

  if (!evidence) {
    return <p className="text-xs text-gray-500">לא נרשמה ראיה בהעברה זו.</p>;
  }

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-gray-400 hover:text-gray-600 underline decoration-dotted"
      >
        {open ? "הסתר ראיות" : "הצג ראיות"}
      </button>
      {open && (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 bg-gray-50 border border-gray-200 rounded p-2 text-gray-600">
          {evidence.tool && (
            <><dt className="font-medium">כלי:</dt>
            <dd data-testid="handoff-evidence-tool">{traceToolLabel(evidence.tool)}</dd></>
          )}
          {evidence.actionStatus && (
            <><dt className="font-medium">סטטוס:</dt><dd>{TRACE_STATUS_LABELS[evidence.actionStatus] || evidence.actionStatus}</dd></>
          )}
          {evidence.source && (
            <><dt className="font-medium">מקור ראיה:</dt>
            <dd data-testid="handoff-evidence-source">{traceEvidenceSourceLabel(evidence.source)}</dd></>
          )}
          {evidence.summary && (<><dt className="font-medium">תמצית:</dt><dd className="break-words">{evidence.summary}</dd></>)}
          {evidence.error && (
            <><dt className="font-medium text-red-500">שגיאה:</dt>
            <dd className="text-red-500" data-testid="handoff-evidence-error">{traceErrorLabel(evidence.error)}</dd></>
          )}
          {evidence.at && (<><dt className="font-medium">מתי:</dt><dd>{formatTime(evidence.at)}</dd></>)}
          {evidence.runId && (
            <>
              <dt className="font-medium">ריצת AI:</dt>
              <dd><Link to={`/agent-runs/${evidence.runId}`} className="text-blue-600 hover:underline">פתח ריצה</Link></dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

function HandoffCard({ event, canManage }) {
  const target = event.target;
  const employeeName = handoffEmployeeName(event);
  const titleId = `handoff-${event.id}-title`;
  const awaiting = isAwaitingOperator(event);
  // One pill, from the rule the page's own tabs partition on — an awaiting card is incoming and
  // says so; a card with no employee to name says nothing about where the handoff went.
  const direction = handoffDirection(event);

  return (
    <article aria-labelledby={titleId} className="bg-white rounded-xl shadow-sm border p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {employeeName ? (
            <EmployeeAvatar
              name={employeeName}
              avatarKey={target?.avatarKey}
              avatarColor={target?.avatarColor}
              size="sm"
            />
          ) : (
            <span aria-hidden="true" className="w-7 h-7 shrink-0 inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-400">?</span>
          )}
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-semibold text-gray-800 truncate">
              {employeeName || "עובד לא ידוע"}
            </h2>
            {target?.roleTitle && <p className="text-xs text-gray-500">{target.roleTitle}</p>}
            {!target && <p className="text-xs text-gray-400">לא ניתן לשייך את ההעברה לעובד</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {direction && (
            <span
              data-testid="handoff-direction"
              className={`text-[11px] px-2 py-0.5 rounded-full ${
                direction === "incoming" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-700"
              }`}
            >
              {HANDOFF_DIRECTION_LABELS[direction]}
            </span>
          )}
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
            {event.sourceLabel}
          </span>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">{formatTime(event.at)}</span>
        </div>
      </div>

      {event.noticeText && <p className="text-sm text-gray-700 leading-relaxed">{event.noticeText}</p>}
      {event.reason && <p className="text-xs text-gray-500">סיבה: {event.reason}</p>}

      <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
        <h3 className="text-xs font-semibold text-gray-600 mb-1">ממצאים</h3>
        {event.findings ? (
          <>
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{event.findings.text}</p>
            <p className="text-[11px] text-gray-400 mt-1">{formatTime(event.findings.at)}</p>
          </>
        ) : (
          <p className="text-sm text-gray-400 italic">
            {target ? "העובד טרם פרסם ממצא אחרי ההעברה." : "אין ממצא משויך — לא ידוע מי קיבל את ההעברה."}
          </p>
        )}
      </div>

      <div className="text-xs">
        <span className="font-medium text-gray-600">ממתין לך: </span>
        {awaiting ? (
          <span className="text-amber-800">{event.awaitingStages.map((s) => s.label).join(" · ")}</span>
        ) : event.employeeSpokeLast ? (
          <span className="text-gray-500">לא נדרש אישור — העובד השיב אחרון וטרם השבת.</span>
        ) : (
          <span className="text-gray-500">לא נדרש אישור — אין בקשה פתוחה מהעובד.</span>
        )}
      </div>
      {!awaiting && event.employeeSpokeLast && event.lastTurnText && (
        <p className="text-xs text-gray-400">תשובת העובד האחרונה: {event.lastTurnText}</p>
      )}

      <EvidenceDrawer event={event} canManage={canManage} />

      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-100">
        <Link
          to={`/conversations/${event.conversationId}`}
          className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs hover:bg-blue-700"
        >
          המשך בשיחה
        </Link>
        {target?.agentId && (
          <Link to={`/agents/${target.agentId}`} className="text-xs text-blue-600 hover:underline">
            פתח סביבת עבודה
          </Link>
        )}
        {event.ticketId && (
          <Link to={`/tickets/${event.ticketId}`} className="text-xs text-blue-600 hover:underline">
            פתח קריאה
          </Link>
        )}
        {!event.ticketId && <span className="text-xs text-gray-400">ללא קריאה משויכת</span>}
        {event.deviceIdentifier && <span className="text-xs text-gray-400">מכשיר: {event.deviceIdentifier}</span>}
      </div>
    </article>
  );
}

export default function HandoffCenterPage() {
  const { can: canAgent } = useAgentPermissions();
  const [tab, setTab] = useState("all");
  const canView = canAgent("agents_view");
  const canManage = canAgent("agents_manage");

  const listQuery = useQuery({
    queryKey: ["handoff-center", "conversations"],
    queryFn: () => conversationsApi.list({ limit: CONVERSATION_SCAN_LIMIT }),
    enabled: canView,
    refetchInterval: LIST_POLL_MS,
  });

  const handoffCandidates = useMemo(() => (listQuery.data || []).filter(isHandoffCandidate), [listQuery.data]);
  const candidates = useMemo(() => handoffCandidates.slice(0, MESSAGE_FETCH_LIMIT), [handoffCandidates]);
  const candidateKey = candidates.map((c) => c.id).join(",");

  const loadsQuery = useQuery({
    queryKey: ["handoff-center", "loads", candidateKey],
    queryFn: async () => {
      const settled = await Promise.allSettled(
        candidates.map(async (conversation) => ({
          conversation,
          messages: await conversationsApi.listMessages(conversation.id),
        })),
      );
      return settled.map((result, index) =>
        result.status === "fulfilled" ? result.value : { conversationId: candidates[index].id, failed: true },
      );
    },
    enabled: canView && candidates.length > 0,
    refetchInterval: TRANSCRIPT_POLL_MS,
  });

  const center = useMemo(() => buildHandoffCenter(loadsQuery.data || []), [loadsQuery.data]);

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במרכז ההעברות</div>;
  }

  const loading = listQuery.isLoading || (candidates.length > 0 && loadsQuery.isLoading);
  const visible = tab === "incoming" ? center.incoming : tab === "outgoing" ? center.outgoing : center.events;
  const truncated = handoffCandidates.length > candidates.length;
  const failedCount = center.failedConversationIds.length;

  return (
    <div className="w-full max-w-[1100px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div>
        <h1 className="text-xl font-semibold text-gray-800">מרכז ההעברות</h1>
        <p className="text-sm text-gray-500 mt-1">
          כל העברות השיחה בין טכנאים לעובדים הדיגיטליים — לאן זה עבר, מה העובד מצא, מה ממתין לך ואיזו ראיה תועדה.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <SummaryTile label="העברות" value={center.events.length} hint="כל העברות השיחה שנמצאו בטווח הנסרק" />
        <SummaryTile label="נכנסות — ממתין לך" value={center.incoming.length} tone="amber" hint="העובד דיווח שהוא ממתין לסקירה או לאישור שלך" />
        <SummaryTile label="יוצאות לעובד" value={center.outgoing.length} tone="blue" hint="הועברו לעובד ואין בקשה פתוחה ממנו" />
        <SummaryTile label="ללא ראיה מתועדת" value={center.withoutEvidence.length} tone="gray" hint="העובד לא כתב רשומת ראיה מובנית אחרי ההעברה" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            aria-pressed={tab === t.value}
            onClick={() => setTab(t.value)}
            className={`text-sm px-3 py-1.5 rounded-full border ${
              tab === t.value ? "bg-brand-600 text-white border-brand-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "incoming" && (
        <p className="text-xs text-gray-500">העברות שהעובד עצמו דיווח בהן שהוא ממתין לסקירה או לאישור שלך.</p>
      )}
      {tab === "outgoing" && (
        <p className="text-xs text-gray-500">העברות שיצאו לעובד ואין בהן בקשה פתוחה ממנו כרגע.</p>
      )}

      <p className="text-xs text-gray-400">
        נסרקו {CONVERSATION_SCAN_LIMIT} השיחות שעודכנו לאחרונה
        {truncated ? `; מתוכן נטענו ${candidates.length} מתוך ${handoffCandidates.length} שיחות עם העברות` : ""}.
      </p>

      {listQuery.isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          שגיאה בטעינת השיחות
          <button type="button" onClick={() => listQuery.refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}
      {failedCount > 0 && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          לא ניתן היה לטעון {failedCount} שיחות — ייתכן שחסרות העברות בטבלה.
        </div>
      )}

      {loading && <p className="text-gray-400 text-sm">טוען...</p>}

      {!loading && visible.length === 0 && (
        <p className="text-gray-400 text-sm italic">
          {center.events.length === 0
            ? "לא נמצאו העברות בטווח הנסרק. העברה נוצרת כשמשהו מועבר לעובד דיגיטלי מתוך שיחה."
            : "אין העברות בקטגוריה הזו."}
        </p>
      )}

      <div className="space-y-3">
        {visible.map((event) => (
          <HandoffCard key={event.id} event={event} canManage={canManage} />
        ))}
      </div>
    </div>
  );
}
